import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { Plus, Edit, CheckCircle, Clock, AlertCircle, ArrowRight, CalendarIcon, ExternalLink, X, Award, TrendingUp, Target, Star, Trash2 } from 'lucide-react';
import { performanceApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

interface EmployeeGoalsViewProps {
  year: number;
  employeeId: string;
  goals: any[];
  reviews: any[];
}

const EmployeeGoalsView: React.FC<EmployeeGoalsViewProps> = ({ year, employeeId, goals, reviews }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<any>(null);
  const [goalFormData, setGoalFormData] = useState({
    title: '',
    description: '',
    target_date: '',
    kpi_metrics: [] as Array<{ metric: string; target: string; unit?: string }>,
    weight: 1,
  });

  // Date picker states
  const [targetDate, setTargetDate] = useState<Date | undefined>(undefined);
  const [reviewStartDate, setReviewStartDate] = useState<Date | undefined>(undefined);
  const [reviewEndDate, setReviewEndDate] = useState<Date | undefined>(undefined);
  const [isTargetDateOpen, setIsTargetDateOpen] = useState(false);
  const [isReviewStartOpen, setIsReviewStartOpen] = useState(false);
  const [isReviewEndOpen, setIsReviewEndOpen] = useState(false);

  // Review dialog state
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({
    review_period_start: '',
    review_period_end: '',
    overall_rating: 0,
    comments: '',
  });

  // Workflow stages
  const workflowStages = [
    { key: 'GOAL_SETTING', label: 'Goal Setting', icon: CheckCircle },
    { key: 'KPI_DEFINITION', label: 'KPI Definition', icon: CheckCircle },
    { key: 'MANAGER_REVIEW', label: 'Manager Review', icon: Clock },
    { key: 'ONE_ON_ONE', label: '1:1 Alignment', icon: Clock },
    { key: 'COMPLETED', label: 'Completed', icon: CheckCircle },
  ];

  // Determine current stage
  const getCurrentStage = () => {
    if (goals.length === 0) return 'GOAL_SETTING';
    const hasKPIs = goals.some((g: any) => g.kpi_metrics);
    const hasReview = reviews.some((r: any) => r.status === 'COMPLETED');
    const hasOneOnOne = goals.some((g: any) => g.one_on_one_date);
    const allApproved = goals.every((g: any) => g.manager_approved);

    if (allApproved && hasOneOnOne) return 'COMPLETED';
    if (hasReview) return 'ONE_ON_ONE';
    if (hasKPIs) return 'MANAGER_REVIEW';
    if (goals.length > 0) return 'KPI_DEFINITION';
    return 'GOAL_SETTING';
  };

  const currentStage = getCurrentStage();

  // Mutations
  const createGoalMutation = useMutation({
    mutationFn: performanceApi.goals.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });
      setIsGoalDialogOpen(false);
      resetGoalForm();
      toast({ title: 'Goal created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateGoalMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => performanceApi.goals.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });
      setIsGoalDialogOpen(false);
      setEditingGoal(null);
      resetGoalForm();
      toast({ title: 'Goal updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const createReviewMutation = useMutation({
    mutationFn: performanceApi.reviews.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceReviews'] });
      setIsReviewDialogOpen(false);
      resetReviewForm();
      toast({ title: 'Review created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleGoalSubmit = () => {
    if (!goalFormData.title || !targetDate) {
      toast({ title: 'Error', description: 'Please fill required fields (Title and Target Date)', variant: 'destructive' });
      return;
    }

    // Filter out empty KPIs and use structured format
    const validKpis = (goalFormData.kpi_metrics || []).filter(
      kpi => kpi.metric && kpi.metric.trim() !== ''
    ).map(kpi => ({
      id: (kpi as any).id || `kpi_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      metric: kpi.metric.trim(),
      target: kpi.target?.trim() || '',
      unit: kpi.unit?.trim() || '',
      progress: (kpi as any).progress || 0,
    }));

    const data = {
      ...goalFormData,
      target_date: format(targetDate, 'yyyy-MM-dd'),
      kpi_metrics: validKpis.length > 0 ? validKpis : undefined,
      employee_id: employeeId,
      year,
      status: 'IN_PROGRESS',
      progress: 0,
      workflow_stage: 'GOAL_SETTING',
    };

    if (editingGoal) {
      updateGoalMutation.mutate({ id: editingGoal.id, data });
    } else {
      createGoalMutation.mutate(data);
    }
  };

  const openGoalDialog = (goal?: any) => {
    if (goal) {
      setEditingGoal(goal);
      setGoalFormData({
        title: goal.title,
        description: goal.description || '',
        target_date: goal.target_date,
        kpi_metrics: goal.kpi_metrics || [],
        weight: goal.weight || 1,
      });
      // Set target date from existing goal
      if (goal.target_date) {
        setTargetDate(new Date(goal.target_date));
      } else {
        setTargetDate(undefined);
      }
    } else {
      resetGoalForm();
    }
    setIsGoalDialogOpen(true);
  };

  const resetGoalForm = () => {
    setGoalFormData({
      title: '',
      description: '',
      target_date: '',
      kpi_metrics: [],
      weight: 1,
    });
    setEditingGoal(null);
    setTargetDate(undefined);
  };

  const handleReviewSubmit = () => {
    if (!reviewStartDate || !reviewEndDate) {
      toast({ title: 'Error', description: 'Please fill required fields (Review Period Start and End)', variant: 'destructive' });
      return;
    }

    // Validate overall_rating
    if (reviewFormData.overall_rating && (reviewFormData.overall_rating < 1 || reviewFormData.overall_rating > 5)) {
      toast({ title: 'Error', description: 'Overall Rating must be between 1 and 5', variant: 'destructive' });
      return;
    }

    const data = {
      ...reviewFormData,
      review_period_start: format(reviewStartDate, 'yyyy-MM-dd'),
      review_period_end: format(reviewEndDate, 'yyyy-MM-dd'),
      employee_id: employeeId,
      reviewer_id: 'current-user-id',
      year,
      status: 'IN_PROGRESS',
      workflow_stage: 'MANAGER_REVIEW',
    };

    createReviewMutation.mutate(data);
  };

  const openReviewDialog = () => {
    resetReviewForm();
    setIsReviewDialogOpen(true);
  };

  const resetReviewForm = () => {
    setReviewFormData({
      review_period_start: '',
      review_period_end: '',
      overall_rating: 0,
      comments: '',
    });
    setReviewStartDate(undefined);
    setReviewEndDate(undefined);
  };

  const getStageStatus = (stageKey: string) => {
    const stages = ['GOAL_SETTING', 'KPI_DEFINITION', 'MANAGER_REVIEW', 'ONE_ON_ONE', 'COMPLETED'];
    const currentIndex = stages.indexOf(currentStage);
    const stageIndex = stages.indexOf(stageKey);

    if (stageIndex < currentIndex) return 'completed';
    if (stageIndex === currentIndex) return 'current';
    return 'pending';
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      PENDING:     'bg-amber-50 text-amber-700 border-amber-200/50 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800/50',
      APPROVED:    'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/50',
      REJECTED:    'bg-rose-50 text-rose-700 border-rose-200/50 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800/50',
      CANCELLED:   'bg-slate-50 text-slate-700 border-slate-200/50 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700/50',
      NOT_STARTED: 'bg-slate-50 text-slate-700 border-slate-200/50 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700/50',
      IN_PROGRESS: 'bg-blue-50 text-blue-700 border-blue-200/50 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/50',
      ON_TRACK:    'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/50',
      COMPLETED:   'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/50',
      AT_RISK:     'bg-rose-50 text-rose-700 border-rose-200/50 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800/50',
      BEHIND:      'bg-rose-50 text-rose-700 border-rose-200/50 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800/50',
    };
    const key = status?.toUpperCase() || 'NOT_STARTED';
    const classNames = statusColors[key as keyof typeof statusColors] || 'bg-slate-50 text-slate-700 border-slate-200/50 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700/50';
    return (
      <Badge className={cn("px-2.5 py-1 rounded-lg font-bold text-[11px] tracking-wide shadow-none border", classNames)} variant="outline">
        {status.replace(/_/g, ' ')}
      </Badge>
    );
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Workflow Progress */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-4 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-500 rounded-xl">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Performance Cycle Progress</CardTitle>
              <p className="text-[11px] text-slate-400 font-bold tracking-widest mt-0.5">Current Phase Tracker for {year}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-8 py-6">
          <div className="flex items-center justify-between py-4 px-2 overflow-x-auto min-w-[600px] custom-scrollbar">
            {workflowStages.map((stage, index) => {
              const status = getStageStatus(stage.key);
              const Icon = stage.icon;

              return (
                <React.Fragment key={stage.key}>
                  <div className="flex flex-col items-center relative group shrink-0">
                    <div
                      className={cn(
                        "w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-sm border",
                        status === 'completed'
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 border-emerald-100 dark:border-emerald-900/50'
                          : status === 'current'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-500 border-blue-100 dark:border-blue-900/50 shadow-blue-500/5 ring-4 ring-blue-50 dark:ring-blue-900/50'
                            : 'bg-slate-50 dark:bg-slate-900/50 text-slate-400 border-slate-100 dark:border-slate-800/50'
                      )}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <p className={cn(
                      "text-[11px] font-black tracking-wider mt-3 text-center max-w-[100px] transition-colors",
                      status === 'current' 
                        ? 'text-blue-500' 
                        : status === 'completed'
                          ? 'text-emerald-500'
                          : 'text-slate-400'
                    )}>
                      {stage.label}
                    </p>
                  </div>
                  {index < workflowStages.length - 1 && (
                    <div className="flex-1 h-[3px] mx-4 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden relative -translate-y-4 shrink-0 min-w-[40px]">
                      <div 
                        className={cn(
                          "h-full rounded-full transition-all duration-500",
                          status === 'completed' ? 'bg-emerald-500 w-full' : 'bg-transparent w-0'
                        )} 
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Goals Section */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-500 rounded-xl">
                <Target className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">My Goals for {year}</CardTitle>
                <p className="text-[11px] text-slate-400 font-bold tracking-widest mt-0.5">Performance objectives and metrics</p>
              </div>
            </div>
            <Button onClick={() => openGoalDialog()}>
              <Plus className="h-4 w-4 mr-1.5" />
              Add Goal
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Goal</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">KPI / Success Metrics</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Target Date</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Progress</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {goals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-20 px-8">
                    <div className="flex flex-col items-center">
                      <div className="bg-slate-50 dark:bg-slate-800 h-16 w-16 rounded-2xl flex items-center justify-center mb-4 border border-slate-100 dark:border-slate-700">
                        <Target className="h-8 w-8 text-slate-300 dark:text-slate-600" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">No goals set</h3>
                      <p className="text-sm text-slate-400 mt-1">Start by adding your first performance goal for {year}.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                goals.map((goal: any) => (
                  <TableRow key={goal.id} className="group border-b border-slate-50 dark:border-slate-800 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors cursor-default">
                    <TableCell className="px-8 py-5">
                      <div>
                        <span className="font-bold text-[#143A62] dark:text-blue-400 group-hover:text-blue-600 transition-colors block text-sm">
                          {goal.title}
                        </span>
                        {goal.description && (
                          <span className="text-xs text-slate-400 mt-1 block italic max-w-sm">
                            {goal.description}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-5">
                      {goal.kpi_metrics && Array.isArray(goal.kpi_metrics) && goal.kpi_metrics.length > 0 ? (
                        <div className="space-y-1.5">
                          {goal.kpi_metrics.map((kpi: any, idx: number) => (
                            <div key={idx} className="text-xs font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-2">
                              <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
                              <span>{kpi.metric}: <span className="font-bold text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800 px-1.5 py-0.5 rounded">{kpi.target}</span></span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs font-bold text-slate-400 italic">Not defined</span>
                      )}
                    </TableCell>
                    <TableCell className="py-5">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                          {format(new Date(goal.target_date), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-5">
                      <div className="flex items-center gap-3">
                        <Progress value={goal.progress || 0} className="w-20 h-1.5" />
                        <span className="text-xs font-black text-[#143A62] dark:text-white bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-md leading-none">{goal.progress || 0}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-5">{getStatusBadge(goal.status)}</TableCell>
                    <TableCell className="px-8 py-5 text-right">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => openGoalDialog(goal)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reviews Section */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-50 dark:bg-purple-900/20 text-purple-500 rounded-xl">
                <Award className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Performance Reviews for {year}</CardTitle>
                <p className="text-[11px] text-slate-400 font-bold tracking-widest mt-0.5">Formal reviews and evaluations</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/performance/reviews')}
              >
                <ExternalLink className="h-4 w-4 mr-1.5" />
                View All Reviews
              </Button>
              <Button 
                onClick={openReviewDialog} 
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Create Review
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-8">
          {reviews.length === 0 ? (
            <div className="text-center py-16">
              <div className="bg-slate-50 dark:bg-slate-800 h-16 w-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-slate-100 dark:border-slate-700">
                <AlertCircle className="h-8 w-8 text-slate-300 dark:text-slate-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">No reviews scheduled</h3>
              <p className="text-sm text-slate-400 mt-1">There are no performance reviews scheduled for you in {year}.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review: any) => (
                <div key={review.id} className="flex items-center justify-between p-5 border border-slate-50 dark:border-slate-800/50 rounded-2xl bg-white dark:bg-slate-800/50 hover:bg-slate-50/50 transition-all duration-300 shadow-sm hover:translate-y-[-1px]">
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center">
                      <span className="w-2 h-2 rounded-full bg-purple-500 mr-2" />
                      Review Period: <span className="text-[#143A62] dark:text-blue-400 ml-1.5 font-black">{format(new Date(review.review_period_start), 'MMM dd')} - {format(new Date(review.review_period_end), 'MMM dd, yyyy')}</span>
                    </p>
                    <p className="text-xs font-bold text-slate-405 dark:text-slate-505 italic max-w-lg truncate">
                      {review.comments || 'No comments yet'}
                    </p>
                  </div>
                  <div className="flex items-center gap-6">
                    {review.overall_rating && (
                      <div className="text-right border-r border-slate-100 dark:border-slate-800 pr-6">
                        <p className="text-2xl font-black text-[#143A62] dark:text-white leading-none">{review.overall_rating}</p>
                        <p className="text-[10px] font-bold text-slate-400 tracking-wider mt-1">out of 5</p>
                      </div>
                    )}
                    {getStatusBadge(review.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Goal Dialog */}
      <Dialog open={isGoalDialogOpen} onOpenChange={setIsGoalDialogOpen}>
        <DialogContent className="w-[min(96vw,800px)] max-w-[800px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 transition-colors duration-300 gap-0 overflow-hidden shadow-2xl">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-500" />
              {editingGoal ? 'Edit Goal' : 'Create New Goal'}
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">Set your performance goals for {year}</DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="title" className="text-xs font-bold text-slate-400 tracking-wider">Goal Title *</Label>
              <Input
                id="title"
                value={goalFormData.title}
                onChange={(e) => setGoalFormData({ ...goalFormData, title: e.target.value })}
                placeholder="e.g., Improve customer satisfaction score"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-xs font-bold text-slate-400 tracking-wider">Description</Label>
              <Textarea
                id="description"
                value={goalFormData.description}
                onChange={(e) => setGoalFormData({ ...goalFormData, description: e.target.value })}
                placeholder="Describe your goal in detail..."
                rows={3}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm"
              />
            </div>

            {/* KPI / Success Metrics Section */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">KPI / Success Metrics</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newKpis = [...(goalFormData.kpi_metrics || []), { metric: '', target: '', unit: '' }];
                    setGoalFormData({ ...goalFormData, kpi_metrics: newKpis });
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add KPI
                </Button>
              </div>

              {goalFormData.kpi_metrics && goalFormData.kpi_metrics.length > 0 ? (
                <div className="space-y-3">
                  {goalFormData.kpi_metrics.map((kpi, index) => (
                    <div key={index} className="flex gap-3 items-end p-4 border border-slate-100 dark:border-slate-850 rounded-2xl bg-slate-50/30 dark:bg-slate-900/50 shadow-sm relative group">
                      <div className="flex-1 space-y-3">
                        <div className="space-y-1">
                          <Label className="text-[10px] font-bold text-slate-400 tracking-widest">Metric Name</Label>
                          <Input
                            placeholder="e.g., Customer Satisfaction"
                            value={kpi.metric || ''}
                            onChange={(e) => {
                              const newKpis = [...goalFormData.kpi_metrics];
                              newKpis[index] = { ...newKpis[index], metric: e.target.value };
                              setGoalFormData({ ...goalFormData, kpi_metrics: newKpis });
                            }}
                            className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-10"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-[10px] font-bold text-slate-400 tracking-widest">Target Value</Label>
                            <Input
                              placeholder="e.g., 90"
                              value={kpi.target || ''}
                              onChange={(e) => {
                                const newKpis = [...goalFormData.kpi_metrics];
                                newKpis[index] = { ...newKpis[index], target: e.target.value };
                                setGoalFormData({ ...goalFormData, kpi_metrics: newKpis });
                              }}
                              className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-10"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px] font-bold text-slate-400 tracking-widest">Unit</Label>
                            <Input
                              placeholder="e.g., %, USD"
                              value={(kpi as any).unit || ''}
                              onChange={(e) => {
                                const newKpis = [...goalFormData.kpi_metrics];
                                newKpis[index] = { ...newKpis[index], unit: e.target.value };
                                setGoalFormData({ ...goalFormData, kpi_metrics: newKpis });
                              }}
                              className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-10"
                            />
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-rose-500 hover:text-rose-600"
                        onClick={() => {
                          const newKpis = goalFormData.kpi_metrics.filter((_, i) => i !== index);
                          setGoalFormData({ ...goalFormData, kpi_metrics: newKpis });
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl text-slate-400 bg-slate-50/20">
                  <p className="text-xs font-bold">No success metrics defined yet</p>
                  <p className="text-[11px] mt-1 font-semibold text-slate-405">Click "Add KPI" to track quantitative metrics for this goal.</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">Target Date *</Label>
                <Popover open={isTargetDateOpen} onOpenChange={setIsTargetDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-medium",
                        !targetDate && "text-slate-400"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                      {targetDate ? format(targetDate, 'PPP') : 'Select target date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-slate-100 dark:border-slate-800 rounded-xl shadow-xl" align="start">
                    <Calendar
                      mode="single"
                      selected={targetDate}
                      onSelect={(date) => {
                        setTargetDate(date);
                        setIsTargetDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="weight" className="text-xs font-bold text-slate-400 tracking-wider">Weight</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  value={goalFormData.weight}
                  onChange={(e) => setGoalFormData({ ...goalFormData, weight: parseFloat(e.target.value) })}
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsGoalDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGoalSubmit}>
              {editingGoal ? 'Update Goal' : 'Create Goal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={isReviewDialogOpen} onOpenChange={setIsReviewDialogOpen}>
        <DialogContent className="w-[min(96vw,800px)] max-w-[800px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 transition-colors duration-300 gap-0 overflow-hidden shadow-2xl">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <Award className="h-5 w-5 text-blue-500" />
              Create Performance Review
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">Create a formal performance review for {year}</DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">Review Period Start *</Label>
                <Popover open={isReviewStartOpen} onOpenChange={setIsReviewStartOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-medium",
                        !reviewStartDate && "text-slate-400"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                      {reviewStartDate ? format(reviewStartDate, 'PPP') : 'Select start date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-slate-100 dark:border-slate-800 rounded-xl shadow-xl" align="start">
                    <Calendar
                      mode="single"
                      selected={reviewStartDate}
                      onSelect={(date) => {
                        setReviewStartDate(date);
                        setIsReviewStartOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">Review Period End *</Label>
                <Popover open={isReviewEndOpen} onOpenChange={setIsReviewEndOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-medium",
                        !reviewEndDate && "text-slate-400"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                      {reviewEndDate ? format(reviewEndDate, 'PPP') : 'Select end date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-slate-100 dark:border-slate-800 rounded-xl shadow-xl" align="start">
                    <Calendar
                      mode="single"
                      selected={reviewEndDate}
                      onSelect={(date) => {
                        setReviewEndDate(date);
                        setIsReviewEndOpen(false);
                      }}
                      disabled={(date) => reviewStartDate ? date < reviewStartDate : false}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="overall_rating" className="text-xs font-bold text-slate-400 tracking-wider">Overall Rating (1-5)</Label>
              <Input
                id="overall_rating"
                type="number"
                min="1"
                max="5"
                step="0.5"
                value={reviewFormData.overall_rating}
                onChange={(e) => {
                  const value = e.target.value ? parseFloat(e.target.value) : 0;
                  if (value === 0 || (value >= 1 && value <= 5)) {
                    setReviewFormData({ ...reviewFormData, overall_rating: value });
                  }
                }}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label htmlFor="comments" className="text-xs font-bold text-slate-400 tracking-wider">Comments</Label>
              <Textarea
                id="comments"
                value={reviewFormData.comments}
                onChange={(e) => setReviewFormData({ ...reviewFormData, comments: e.target.value })}
                placeholder="Enter review comments and feedback..."
                rows={5}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm"
              />
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReviewSubmit}>
              Create Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeGoalsView;
